'use client'

import { useEffect } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import { useCart } from '@/contexts/cart'
import { trackEvent, trackPageView } from '@/lib/tracking'

/**
 * Hook to track page views automatically
 */
export function usePageViewTracking(pageName?: string) {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  
  useEffect(() => {
    // Use the provided page name or default to the pathname
    const name = pageName || pathname
    
    // Extract search params into an object
    const queryParams: Record<string, string> = {}
    if (searchParams) {
      searchParams.forEach((value, key) => {
        queryParams[key] = value
      })
    }
    
    trackPageView(name, {
      query_params: Object.keys(queryParams).length > 0 ? queryParams : undefined
    })
  }, [pathname, searchParams, pageName])
}

type MinimalVariant = {
  id: string
  sku?: string | null
  title?: string | null
  prices?: Array<{
    amount: number
    currency_code: string
  }>
}

type MinimalCategory = {
  id: string
  name: string
}

export type MinimalProduct = {
  id: string
  title?: string
  handle?: string
  variants?: MinimalVariant[] | null
  categories?: MinimalCategory[] | null
  store_id?: string
  store_name?: string
}

/**
 * Hook for product tracking
 */
export function useProductTracking() {
  return {
    trackProductView: (product: MinimalProduct) => {
      trackEvent('Product Viewed', {
        product_id: product.id,
        product_name: product.title,
        product_handle: product.handle,
        variant_id: product.variants?.[0]?.id,
        sku: product.variants?.[0]?.sku,
        price: product.variants?.[0]?.prices?.[0]?.amount,
        currency: product.variants?.[0]?.prices?.[0]?.currency_code,
        category_id: product.categories?.[0]?.id,
        category_name: product.categories?.[0]?.name,
        store_id: product.store_id,
        store_name: product.store_name
      })
    },
    
    trackProductAddedToCart: (product: {
      id: string
      title?: string
      variant_id: string
      quantity: number
      price?: number
      currency?: string
      store_id?: string
    }) => {
      trackEvent('Product Added', {
        product_id: product.id,
        product_name: product.title,
        variant_id: product.variant_id,
        quantity: product.quantity,
        price: product.price,
        currency: product.currency,
        store_id: product.store_id
      })
    }
  }
}

/**
 * Hook for cart tracking
 */
export function useCartTracking() {
  const { cart } = useCart()
  
  return {
    trackCartViewed: () => {
      if (!cart) return
      
      trackEvent('Cart Viewed', {
        cart_id: cart.id,
        items_count: cart.items?.length || 0,
        total: cart.total,
        currency: cart.region?.currency_code,
        store_ids: cart.items?.map(item => item.metadata?.store_id).filter(Boolean)
      })
    },
    
    trackCartItemAdded: (item: {
      id: string
      product_id: string
      variant_id: string
      quantity: number
      unit_price: number
      metadata?: Record<string, any>
    }) => {
      trackEvent('Cart Item Added', {
        cart_id: cart?.id,
        item_id: item.id,
        product_id: item.product_id,
        variant_id: item.variant_id,
        quantity: item.quantity,
        price: item.unit_price,
        store_id: item.metadata?.store_id
      })
    },
    
    trackCartItemRemoved: (item: {
      id: string
      product_id: string
      variant_id: string
      quantity: number
    }) => {
      trackEvent('Cart Item Removed', {
        cart_id: cart?.id,
        item_id: item.id,
        product_id: item.product_id,
        variant_id: item.variant_id,
        quantity: item.quantity
      })
    }
  }
}

/**
 * Hook for checkout tracking
 */
export function useCheckoutTracking() {
  const { cart } = useCart()
  
  return {
    trackCheckoutStarted: () => {
      if (!cart) return
      
      trackEvent('Checkout Started', {
        cart_id: cart.id,
        items_count: cart.items?.length || 0,
        total: cart.total,
        currency: cart.region?.currency_code,
        store_ids: cart.items?.map(item => item.metadata?.store_id).filter(Boolean)
      })
    },
    
    trackCheckoutStepViewed: (step: string, stepNumber: number) => {
      if (!cart) return
      
      trackEvent('Checkout Step Viewed', {
        cart_id: cart.id,
        step_name: step,
        step_number: stepNumber
      })
    },
    
    trackCheckoutStepCompleted: (step: string, stepNumber: number) => {
      if (!cart) return
      
      trackEvent('Checkout Step Completed', {
        cart_id: cart.id,
        step_name: step,
        step_number: stepNumber
      })
    },
    
    trackPaymentMethodSelected: (method: string) => {
      if (!cart) return
      
      trackEvent('Payment Method Selected', {
        cart_id: cart.id,
        payment_method: method
      })
    },
    
    trackOrderCompleted: (orderId: string) => {
      if (!cart) return
      
      trackEvent('Order Completed', {
        order_id: orderId,
        cart_id: cart.id,
        items: cart.items?.map(item => ({
          product_id: item.product_id,
          variant_id: item.variant_id,
          quantity: item.quantity,
          price: item.unit_price,
          store_id: item.metadata?.store_id
        })),
        total: cart.total,
        subtotal: cart.subtotal,
        tax_total: cart.tax_total,
        shipping_total: cart.shipping_total,
        discount_total: cart.discount_total,
        currency: cart.region?.currency_code
      })
    }
  }
} 